import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusCircle, Users as UsersIcon } from 'lucide-react';
import { usersApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input, Label } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Badge } from '../components/ui/badge';
import { WhatsThis } from '../components/ui/tooltip';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { ROLE_DESCRIPTIONS, can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import type { MemberSummary, Role } from '../lib/types';

const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', admin: 'Admin', operator: 'Operator', viewer: 'Viewer' };
const ALL_ROLES: Role[] = ['owner', 'admin', 'operator', 'viewer'];

function AddUserForm({ isOwner, onDone }: { isOwner: boolean; onDone: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('viewer');
  const [error, setError] = useState<string | null>(null);

  const assignableRoles = isOwner ? ALL_ROLES : ALL_ROLES.filter((r) => r !== 'owner');

  const mutation = useMutation({
    mutationFn: () => usersApi.invite({ email, name, role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: 'User added', description: `${email} can now sign in with the ${ROLE_LABEL[role]} role.`, variant: 'success' });
      onDone();
    },
    onError: (err) => setError(friendlyErrorMessage(err)),
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        mutation.mutate();
      }}
    >
      <div className="space-y-1">
        <Label htmlFor="new-user-email">Email</Label>
        <Input id="new-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="them@company.com" required />
        <p className="text-xs text-ink-muted">This is the identity they'll use to sign in (via SSO or password).</p>
      </div>
      <div className="space-y-1">
        <Label htmlFor="new-user-name">Name</Label>
        <Input id="new-user-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="space-y-1">
        <Label htmlFor="new-user-role">Role</Label>
        <Select value={role} onValueChange={(v) => setRole(v as Role)}>
          <SelectTrigger id="new-user-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {assignableRoles.map((r) => (
              <SelectItem key={r} value={r}>
                {ROLE_LABEL[r]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-ink-muted">{ROLE_DESCRIPTIONS[role]}</p>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <Button type="submit" className="w-full" disabled={mutation.isPending}>
        Add user
      </Button>
    </form>
  );
}

function MemberRow({ member, isOwner }: { member: MemberSummary; isOwner: boolean }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const assignableRoles = isOwner ? ALL_ROLES : ALL_ROLES.filter((r) => r !== 'owner' || member.role === 'owner');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const roleMutation = useMutation({
    mutationFn: (role: Role) => usersApi.updateRole(member.userId, role),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Role updated', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not update role', description: friendlyErrorMessage(err), variant: 'error' }),
  });
  const statusMutation = useMutation({
    mutationFn: () => usersApi.updateStatus(member.userId, member.status === 'active' ? 'disabled' : 'active'),
    onSuccess: () => {
      invalidate();
      toast({ title: member.status === 'active' ? 'Access paused' : 'Access restored', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not change access', description: friendlyErrorMessage(err), variant: 'error' }),
  });
  const removeMutation = useMutation({
    mutationFn: () => usersApi.remove(member.userId),
    onSuccess: () => {
      invalidate();
      toast({ title: 'User removed from workspace', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not remove user', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
        <div className="min-w-0">
          <p className="truncate font-medium text-ink">{member.name ?? member.email ?? member.userId}</p>
          <p className="truncate text-xs text-ink-muted">{member.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={member.status === 'active' ? 'success' : 'neutral'}>{member.status === 'active' ? 'Active' : 'Paused'}</Badge>
          <Select value={member.role} onValueChange={(v) => roleMutation.mutate(v as Role)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {assignableRoles.map((r) => (
                <SelectItem key={r} value={r}>
                  {ROLE_LABEL[r]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ConfirmDialog
            trigger={
              <Button variant="outline" size="sm">
                {member.status === 'active' ? 'Pause access' : 'Restore access'}
              </Button>
            }
            title={member.status === 'active' ? 'Pause this user\'s access?' : "Restore this user's access?"}
            description={
              member.status === 'active'
                ? "They'll immediately be signed out of new sessions and won't be able to sign back in until you restore access. Nothing is deleted."
                : "They'll be able to sign in again immediately."
            }
            confirmLabel={member.status === 'active' ? 'Pause access' : 'Restore access'}
            variant={member.status === 'active' ? 'danger' : 'primary'}
            onConfirm={() => statusMutation.mutateAsync()}
          />
          <ConfirmDialog
            trigger={
              <Button variant="danger" size="sm">
                Remove
              </Button>
            }
            title="Remove this user from the workspace?"
            description="They'll lose all access to this workspace immediately. This cannot be undone — you'd need to add them again from scratch."
            confirmLabel="Remove"
            variant="danger"
            onConfirm={() => removeMutation.mutateAsync()}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export function Users() {
  const { principal } = useAuth();
  const isOwner = principal?.role === 'owner';
  const [addOpen, setAddOpen] = useState(false);
  const query = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const members = query.data?.users ?? [];
  const canManage = principal?.role === undefined || can(principal.role, 'user:manage');

  return (
    <div>
      <PageHeader
        title="Users & Workspace"
        description="Who has access to this workspace, and what they can do."
        action={
          canManage ? (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle size={16} /> Add user
                </Button>
              </DialogTrigger>
              <DialogContent title="Add a user" description="They'll sign in using the email below.">
                <AddUserForm isOwner={isOwner} onDone={() => setAddOpen(false)} />
              </DialogContent>
            </Dialog>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap gap-4 rounded-xl bg-surface-sunken p-4 text-xs text-ink-muted">
        {ALL_ROLES.map((r) => (
          <span key={r}>
            <span className="font-medium text-ink">{ROLE_LABEL[r]}:</span> {ROLE_DESCRIPTIONS[r]}
          </span>
        ))}
      </div>

      {members.length === 0 ? (
        <EmptyState icon={<UsersIcon size={32} />} title="No members yet" />
      ) : (
        <div className="space-y-2">
          {members.map((member) =>
            canManage ? (
              <MemberRow key={member.userId} member={member} isOwner={isOwner} />
            ) : (
              <Card key={member.userId}>
                <CardContent className="flex items-center justify-between pt-6">
                  <div>
                    <p className="font-medium text-ink">{member.name ?? member.email}</p>
                    <p className="text-xs text-ink-muted">{member.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={member.status === 'active' ? 'success' : 'neutral'}>
                      {member.status === 'active' ? 'Active' : 'Paused'}
                    </Badge>
                    <Badge variant="brand">{ROLE_LABEL[member.role]}</Badge>
                  </div>
                </CardContent>
              </Card>
            ),
          )}
        </div>
      )}
      <p className="mt-4 text-xs text-ink-faint">
        <WhatsThis>Only a workspace Owner can promote someone to Owner, and every workspace must always have at least one active Owner.</WhatsThis>{' '}
        Role changes are protected by a few workspace-safety rules.
      </p>
    </div>
  );
}
